import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopBar from './Components/TopBar';
import SummaryDetails from './Components/SummaryDetails/SummaryDetails';
import StakeholderMatters from './Components/StakeholderMatters/StakeholderMatters';
import StakeholderActionItems from './Components/StakeholderActionItems/StakeholderActionItems';
import StakeholderFinancials from './Components/StakeholderFinancials/StakeholderFinancials';
import CreateTasksPage from '../../Tasks/Tasks/CreateTasks/CreateTasksPage';
import EditStakeholderModal from './Components/EditStakeholderModal/EditStakeholderModal';
import OrgMsaRatesCard from './Components/OrgMsaRatesCard/OrgMsaRatesCard';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import OrganizationService from '@frontend/AppSettings/services/OrganizationService';
import {
  OrganizationAnchorContext,
  OrganizationAnchor,
} from './OrganizationAnchorContext';
import './StakeholderDetails.css';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const StakeholderDetails: React.FC = () => {
  const { stakeholderId, type } = useParams<{ stakeholderId: string; type: 'individual' | 'organization' }>();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('summary');
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [stakeholderData, setStakeholderData] = useState<any>(null);
  const [orgAnchor, setOrgAnchor] = useState<OrganizationAnchor | 'loading' | null>(null);

  // P2 Build J: an organization route now carries a REAL org id (UUID). Legacy
  // `org-<clientid>` ids remain for the disclosed not-yet-linked groups and for
  // bookmarked pre-P2 deep links (which the shim below redirects through the join).
  const isRealOrg = type === 'organization' && UUID_RE.test(stakeholderId ?? '');
  const isLegacyOrg = type === 'organization' && (stakeholderId ?? '').startsWith('org-');

  useEffect(() => {
    let cancelled = false;
    if (isLegacyOrg) {
      // Redirect shim: a pre-P2 bookmark's client resolves through the join to its real
      // org. An UNLINKED client's group has no join row — it stays on the legacy
      // string-group rendering below, disclosed, never dropped.
      const clientId = (stakeholderId ?? '').replace('org-', '');
      OrganizationService.getOrganizationsByMember(clientId)
        .then((orgs) => {
          if (!cancelled && orgs.length > 0) {
            navigate(`/stakeholder/organization/${orgs[0].id}`, { replace: true });
          }
        })
        .catch(() => {
          /* stay on the legacy rendering */
        });
      return () => {
        cancelled = true;
      };
    }
    if (isRealOrg) {
      setOrgAnchor('loading');
      Promise.all([
        OrganizationService.getOrganization(stakeholderId!),
        OrganizationService.getMembers(stakeholderId!),
      ])
        .then(([organization, members]) => {
          if (cancelled) return;
          const primary = members.find((m) => m.is_primary) ?? members[0];
          setOrgAnchor({
            organization,
            anchorId: primary ? `org-${primary.clientid}` : null,
          });
        })
        .catch(() => {
          if (!cancelled) setOrgAnchor(null);
        });
      return () => {
        cancelled = true;
      };
    }
    setOrgAnchor(null);
    return () => {
      cancelled = true;
    };
  }, [stakeholderId, type, isRealOrg, isLegacyOrg, navigate]);

  // What the legacy-shaped children render from: the primary member's anchor for a
  // real org; the route id (individual, or legacy group) otherwise.
  const childId =
    isRealOrg && orgAnchor && orgAnchor !== 'loading' && orgAnchor.anchorId
      ? orgAnchor.anchorId
      : stakeholderId!;

  // Redirect away from financials page if in engineering environment
  useEffect(() => {
    if (isEngineeringEnvironment && activePage === 'financials') {
      setActivePage('summary');
    }
  }, [activePage]);

  if (isRealOrg && orgAnchor === 'loading') {
    return (
      <div className="stakeholder-details-page">
        <div className="stakeholder-content">Loading organization…</div>
      </div>
    );
  }

  if (isRealOrg && orgAnchor && orgAnchor !== 'loading' && !orgAnchor.anchorId) {
    // A real org with no linked contacts yet: honest empty state instead of rendering
    // the contact-shaped children against nothing. The MSA card still renders (P2 Build M,
    // engineering only) — an admin-minted org is exactly where rates are entered before
    // contacts link.
    return (
      <div className="stakeholder-details-page">
        <div className="stakeholder-content">
          <h2>{orgAnchor.organization.name}</h2>
          <p>No contacts are linked to this organization yet.</p>
          {isEngineeringEnvironment && <OrgMsaRatesCard orgId={stakeholderId!} />}
        </div>
      </div>
    );
  }

  // The org-level MSA card (P2 Build M): the org detail's summary view, engineering only —
  // MSA rates overlay the bids catalogs, which the legal vertical does not carry.
  const msaCard = isRealOrg && isEngineeringEnvironment
    ? <OrgMsaRatesCard orgId={stakeholderId!} />
    : null;

  const renderActivePage = () => {
    // Don't render financials in engineering environment
    if (isEngineeringEnvironment && activePage === 'financials') {
      return (
        <>
          <SummaryDetails stakeholderId={childId} type={type || 'individual'} />
          {msaCard}
        </>
      );
    }

    switch (activePage) {
      case 'summary':
        return (
          <>
            <SummaryDetails stakeholderId={childId} type={type || 'individual'} />
            {msaCard}
          </>
        );
      case 'matters':
        return <StakeholderMatters stakeholderId={childId} type={type || 'individual'} />;
      case 'actionItems':
        return <StakeholderActionItems stakeholderId={childId} type={type || 'individual'} />;
      case 'financials':
        return <StakeholderFinancials stakeholderId={childId} type={type || 'individual'} />;
      default:
        return (
          <>
            <SummaryDetails stakeholderId={childId} type={type || 'individual'} />
            {msaCard}
          </>
        );
    }
  };

  return (
    <OrganizationAnchorContext.Provider
      value={isRealOrg && orgAnchor && orgAnchor !== 'loading' ? orgAnchor : null}
    >
      <div className="stakeholder-details-page">
        <TopBar
          setActivePage={setActivePage}
          activePage={activePage}
          stakeholderId={childId}
          type={type || 'individual'}
          onOpenCreateTaskModal={() => setIsCreateTaskModalOpen(true)}
          onOpenEditModal={() => setIsEditModalOpen(true)}
          onStakeholderDataChange={setStakeholderData}
        />
        <div className="stakeholder-content">{renderActivePage()}</div>
        {isCreateTaskModalOpen && (
          <CreateTasksPage onClose={() => setIsCreateTaskModalOpen(false)} />
        )}
        {isEditModalOpen && stakeholderData && (
          <EditStakeholderModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            stakeholderId={childId}
            type={type || 'individual'}
            stakeholderData={stakeholderData}
            onUpdateSuccess={() => {
              // Refresh the page data by triggering a re-fetch
              window.location.reload();
            }}
          />
        )}
      </div>
    </OrganizationAnchorContext.Provider>
  );
};

export default StakeholderDetails;
